import { useCallback, useRef, type TouchEventHandler } from 'react'

interface SwipeHandlers {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  /** Minimum horizontal distance (px) before a swipe is recognized. */
  threshold?: number
  /** Ignore vertical-dominant gestures (scroll). */
  maxVerticalRatio?: number
}

interface SwipeBind {
  onTouchStart: TouchEventHandler
  onTouchEnd: TouchEventHandler
}

/**
 * Lightweight touch swipe recognizer for drawer open/close and tab switches.
 * Prefer native scroll for vertical axes; only fires on clear horizontal intent.
 */
export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  threshold = 56,
  maxVerticalRatio = 0.6,
}: SwipeHandlers): SwipeBind {
  const startX = useRef(0)
  const startY = useRef(0)

  const onTouchStart = useCallback<TouchEventHandler>((event) => {
    const touch = event.changedTouches[0]
    if (!touch) return
    startX.current = touch.clientX
    startY.current = touch.clientY
  }, [])

  const onTouchEnd = useCallback<TouchEventHandler>(
    (event) => {
      const touch = event.changedTouches[0]
      if (!touch) return

      const dx = touch.clientX - startX.current
      const dy = touch.clientY - startY.current
      const absX = Math.abs(dx)
      const absY = Math.abs(dy)

      if (absX < threshold) return
      if (absY / absX > maxVerticalRatio) return

      if (dx > 0) {
        onSwipeRight?.()
      } else {
        onSwipeLeft?.()
      }
    },
    [maxVerticalRatio, onSwipeLeft, onSwipeRight, threshold],
  )

  return { onTouchStart, onTouchEnd }
}
